import keyBy from 'lodash/keyBy'
import { useRouter } from 'next/router'
import { FullWidthLoadingFallback } from '@omnea-app/fe-render-boundaries/LoadingFallbacks'
import { NotFoundBoundary } from '@omnea-app/fe-render-boundaries/NotFoundBoundary'
import { OrganisationSwitchScreen } from '@omnea-app/fe-render-boundaries/OrganisationSwitchScreen'
import { useAvailableOrganisations } from './useAvailableOrganisations'
import { usePotentiallyAuthenticatedUser } from './UserSessionProvider'
import { useSwitchOrganisation } from './useSwitchOrganisation'
import type { OrganisationMismatchProps } from '@omnea-app/fe-render-boundaries/OrganisationMismatch'

interface Props extends OrganisationMismatchProps {
  /** Path to redirect to after switching. If not provided, defaults to home. */
  redirectTo?: string
}

export const OrganisationSwitchBoundary: React.FC<Props> = ({ correctOrganisationId, resourceType, redirectTo }) => {
  const currentSession = usePotentiallyAuthenticatedUser()
  const { reload } = useRouter()

  const { data, isLoading, isError } = useAvailableOrganisations()

  const switchOrganisation = useSwitchOrganisation()

  const handleClick = async () => {
    await switchOrganisation(correctOrganisationId, { redirectTo })
    reload()
  }

  if (isLoading) {
    return <FullWidthLoadingFallback />
  }

  if (isError || !data || !currentSession.org) {
    return <NotFoundBoundary />
  }

  const availableOrganisationsById = keyBy(data.availableOrganisations, 'id')
  const currentOrg = availableOrganisationsById[currentSession.org.organisationId]
  const correctOrg = availableOrganisationsById[correctOrganisationId]

  if (!currentOrg || !correctOrg) {
    return <NotFoundBoundary />
  }

  return (
    <OrganisationSwitchScreen
      currentOrganisationName={currentOrg.name}
      correctOrganisationName={correctOrg.name}
      resourceType={resourceType}
      onSwitch={handleClick}
    />
  )
}
